import { z } from "zod";

type EnvironmentSchema = z.infer<typeof environmentSchema>;
type RemoveVitePrefix<T> = {
  [K in keyof T as K extends `VITE_${infer U}` ? U : K]: T[K];
};
type AppModeSchema = z.infer<typeof appMode>;

const appMode = z.union([
  z.literal("local"),
  z.literal("nightly"),
  z.literal("staging"),
  z.literal("production"),
]);

const httpsOrLocalhost = z
  .string()
  .refine((v) => v.startsWith("https://") || v.startsWith("http://localhost"));

const environmentSchema = z
  .object({
    VITE_APP_MODE: appMode,
    VITE_AUTH0_AUDIENCE: z.string(),
    VITE_AUTH0_CLIENT_ID: z.string(),
    VITE_AUTH0_DOMAIN: z.string(),
    VITE_DIGITALNI_SKLADY_API_URL: httpsOrLocalhost,
    VITE_FIREBASE_API_KEY: z.string(),
    VITE_FIREBASE_APP_ID: z.string(),
    VITE_FIREBASE_AUTH_DOMAIN: z.string(),
    VITE_FIREBASE_MEASUREMENT_ID: z.string(),
    VITE_FIREBASE_MESSAGING_SENDER_ID: z.string(),
    VITE_FIREBASE_PROJECT_ID: z.string(),
    VITE_FIREBASE_STORAGE_BUCKET: z.string(),
    VITE_FIREBASE_VAPID_KEY: z.string(),
    VITE_GOOGLE_ANALYTICS_ID: z.string().optional(),
    VITE_INTERCOM_APP_ID: z.string().optional(),
    VITE_MIXPANEL_TOKEN: z.string(),
    VITE_NEWS_API_URL: httpsOrLocalhost,
    VITE_NEWS_OPENAPI_URL: httpsOrLocalhost,
    VITE_NOTIFICATIONS_API_URL: httpsOrLocalhost,
    VITE_NOTIFICATIONS_OPENAPI_URL: httpsOrLocalhost,
    VITE_PRICES_API_URL: httpsOrLocalhost,
    VITE_PRICES_OPENAPI_URL: httpsOrLocalhost,
    VITE_RELEASE_ID: z.string().optional(),
    VITE_UNLEASH_APP_NAME: z.string(),
    VITE_UNLEASH_CLIENT_KEY: z.string(),
    VITE_UNLEASH_ENVIRONMENT: z.enum(["nightly", "staging", "production"]),
    VITE_UNLEASH_PROXY_URL: httpsOrLocalhost,
    VITE_USERS_API_URL: httpsOrLocalhost,
    VITE_USERS_OPENAPI_URL: httpsOrLocalhost,
  })
  .superRefine((data, ctx) => {
    if (data.VITE_APP_MODE !== "staging" && !data.VITE_INTERCOM_APP_ID) {
      ctx.addIssue({
        code: "custom",
        message:
          // eslint-disable-next-line lingui/no-unlocalized-strings
          "VITE_INTERCOM_APP_ID is required when APP_MODE is not staging",
        path: ["VITE_INTERCOM_APP_ID"],
      });
    }
  });

// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
const envLike = import.meta.env ?? process?.env;
const safeEnvironment = environmentSchema.safeParse(envLike);

if (!safeEnvironment.success) {
  // eslint-disable-next-line no-console
  console.error(
    "Error: the app environment is not in tact. Check env.ts file.\n",
  );
  safeEnvironment.error.issues.map((issue) =>
    // eslint-disable-next-line no-console
    console.error(JSON.stringify(issue) + "\n"),
  );
  throw new Error("Error: the app environment is not in tact.");
}

const getEnvironmentVariable = <
  K extends keyof RemoveVitePrefix<EnvironmentSchema>,
>(
  key: K,
) => safeEnvironment.data[`VITE_${key}`];

export { getEnvironmentVariable };
export type { AppModeSchema };
